import React, { useEffect, useState, useRef } from "react";
import ContentLoader from "../../components/ContentLoader";
import { AssessmentQuestionNav } from "../../components/assessment/AssessmentQuestionNav";
import { getAssessmentDetails, getSubCapabilityAssessmentData } from "../../services/assessment";
import { useAuth } from "../../auth/AuthContext";
import { useAssessmentFormConfig, getSubCapabilityFormConfig } from "../../hooks/useAssessmentFormConfig";
import {
  ratingToStateKey,
  stateKeyToRating,
  getMaturityLabel,
} from "../../utils/assessmentFormConfig";
import {
  assessmentUnitKey,
  assessmentUnitTitle,
  findAssessmentRow,
  flattenAssessmentRows,
  isCapabilityNavHighlighted,
  isLeafSelectedCapability,
  isLeafUnit,
  lookupUnitState,
  sameAssessmentUnit,
} from "../../utils/assessmentUnitUtils";

const EMPTY_SUB_CAPABILITY_STATE = {
  currentState: "",
  futureState: "",
  currentStatusDescription: "",
  futureStatusDescription: "",
  businessValue: "",
  businessValueImpact: "",
};

function Assess({ user: userProp, embedded = false, isTabActive = true }) {
  const basename = process.env.PUBLIC_URL || "";
  const { user: authUser } = useAuth();
  const user = userProp || authUser;

  const resolvedClientUserId = user?.userId ?? null;
  const resolvedProjectId = user?.project
    ? Number(
        user.project.projectId ??
          user.project.ProjectId ??
          user.project.id ??
          NaN
      )
    : null;
  const hasEmbeddedProject =
    embedded && user?.project && Number.isFinite(resolvedProjectId);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCapability, setExpandedCapability] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [questionIndex, setQuestionIndex] = useState(null);
  const [subCapabilityState, setSubCapabilityState] = useState({
    currentState: "",
    futureState: "",
    currentStatusDescription: "",
    futureStatusDescription: "",
    businessValue: "",
    businessValueImpact: "",
  });
  const [selectedSubCapability, setSelectedSubCapability] = useState({
    capabilityId: null,
    subCapabilityId: null,
    subCapability: "",
  });
  const [selectedCard, setSelectedCard] = useState(null);
  const [subSelectedCard, setSubSelectedCard] = useState(null);
  const [subCapabilityStates, setSubCapabilityStates] = useState({});
  const [dirtySubCapabilities, setDirtySubCapabilities] = useState({});
  const dropdownRefs = useRef({});
  const body = document.body;

  const activeTextAreaRef = useRef(null);
  const [projectId, setProjectId] = useState(null);
  const [userId, setUserId] = useState(null);

  const { formConfig } = useAssessmentFormConfig(data, selectedSubCapability);
  const selectedSubCapabilityRef = useRef(selectedSubCapability);
  selectedSubCapabilityRef.current = selectedSubCapability;

  const apiCallTracker = useRef({
    assessmentData: false,
    requestStatus: false,
  });
  const loadedContextRef = useRef("");

  useEffect(() => {
    if (resolvedClientUserId) {
      setUserId(resolvedClientUserId);
    }

    if (hasEmbeddedProject) {
      setProjectId(resolvedProjectId);
      return;
    }

    const storedProjectId = localStorage.getItem("projectId");
    if (storedProjectId) {
      setProjectId(parseInt(storedProjectId, 10));
    }
  }, [resolvedClientUserId, resolvedProjectId, hasEmbeddedProject]);

  useEffect(() => {
    if (!projectId || !userId) {
      return;
    }

    const contextKey = `${projectId}:${userId}`;
    if (loadedContextRef.current !== contextKey) {
      loadedContextRef.current = contextKey;
      apiCallTracker.current.assessmentData = false;
      setData(null);
      setLoading(true);
      if (embedded) {
        setShowModal(false);
      }
    }

    fetchAssessmentData(projectId);
  }, [projectId, userId, embedded]);

  useEffect(() => {
    if (
      data &&
      data.data &&
      data.data.selectedCapabilities &&
      data.data.selectedCapabilities.length > 0 &&
      !selectedSubCapability.capabilityId
    ) {
      const firstCapability = data.data.selectedCapabilities[0];
      if (
        firstCapability.selectedSubCapabilities &&
        firstCapability.selectedSubCapabilities.length > 0
      ) {
        const firstSubCapability = firstCapability.selectedSubCapabilities[0];
        setSelectedSubCapability({
          capabilityId: firstCapability.capabilityId,
          subCapabilityId: firstSubCapability.subCapabilityId,
          subCapability: firstSubCapability.subCapability,
        });
        setSelectedCard(firstCapability.capabilityId);
        setSubSelectedCard(
          assessmentUnitKey(
            firstCapability.capabilityId,
            firstSubCapability.subCapabilityId
          )
        );
        setExpandedCapability(firstCapability.capabilityId);
      }
    }
  }, [data, selectedSubCapability, projectId]);

  const fetchAssessmentData = async (projectId) => {
    if (apiCallTracker.current.assessmentData) return;
    apiCallTracker.current.assessmentData = true;

    try {
      const payload = { userId, projectId };

      const [assessmentData, capabilitiesData] = await Promise.all([
        getSubCapabilityAssessmentData(payload),
        getAssessmentDetails(payload),
      ]);

      setData(capabilitiesData);

      const assessmentRows = flattenAssessmentRows(assessmentData?.data);

      if (capabilitiesData?.data?.selectedCapabilities) {
        const newSubCapabilityStates = {};

        capabilitiesData.data.selectedCapabilities.forEach((capability) => {
          (capability.selectedSubCapabilities || []).forEach((subCapability) => {
            const matchedSubCapability = findAssessmentRow(
              assessmentRows,
              capability.capabilityId,
              subCapability.subCapabilityId
            );

            const subFormConfig = getSubCapabilityFormConfig(subCapability);

            const subCapabilityStateData = matchedSubCapability
              ? {
                  currentState: ratingToStateKey(
                    matchedSubCapability.currentStatusRating,
                    subFormConfig
                  ),
                  futureState: ratingToStateKey(
                    matchedSubCapability.futureStatusRating,
                    subFormConfig
                  ),
                  currentStatusDescription:
                    matchedSubCapability.currentStatusDescription,
                  futureStatusDescription:
                    matchedSubCapability.futureStatusDescription,
                  businessValue: matchedSubCapability.businessValue || "",
                  businessValueImpact: matchedSubCapability.businessValueImpact || "",
                }
              : { ...EMPTY_SUB_CAPABILITY_STATE };

            newSubCapabilityStates[
              assessmentUnitKey(capability.capabilityId, subCapability.subCapabilityId)
            ] = subCapabilityStateData;
          });
        });

        setSubCapabilityStates(newSubCapabilityStates);

        const selected = selectedSubCapabilityRef.current;
        const activeState = lookupUnitState(
          newSubCapabilityStates,
          selected?.capabilityId,
          selected?.subCapabilityId
        );
        if (activeState) {
          setSubCapabilityState(activeState);
        }
      }
    } catch (error) {
      console.error("Error fetching assessment data:", error.message);
    } finally {
      apiCallTracker.current.assessmentData = false;
      setLoading(false);
    }
  };

  const getModifiedSubCapabilities = () => {
    const capabilities = data?.data?.selectedCapabilities ?? [];
    const modified = [];

    capabilities.forEach((capability) => {
      (capability.selectedSubCapabilities || []).forEach((subCapability) => {
        const unitKey = assessmentUnitKey(
          capability.capabilityId,
          subCapability.subCapabilityId
        );
        if (!dirtySubCapabilities[unitKey]) return;

        const isCurrent = sameAssessmentUnit(
          capability.capabilityId,
          subCapability.subCapabilityId,
          selectedSubCapability?.capabilityId,
          selectedSubCapability?.subCapabilityId
        );
        const subCapabilityData = isCurrent
          ? subCapabilityState
          : subCapabilityStates[unitKey] || EMPTY_SUB_CAPABILITY_STATE;

        modified.push({
          capabilityId: capability.capabilityId,
          subCapabilityId: isLeafUnit(subCapability.subCapabilityId)
            ? null
            : Number(subCapability.subCapabilityId),
          currentStatusDescription: subCapabilityData.currentStatusDescription,
          futureStatusDescription: subCapabilityData.futureStatusDescription,
          businessValue: subCapabilityData.businessValue,
          businessValueImpact: subCapabilityData.businessValueImpact,
          currentStatusRating: stateKeyToRating(
            subCapabilityData.currentState,
            formConfig
          ),
          futureStatusRating: stateKeyToRating(
            subCapabilityData.futureState,
            formConfig
          ),
        });
      });
    });

    return modified;
  };

  const handleBackQuestion = async () => {
    if (questionIndex > 0) {
      const capabilities = data.data.selectedCapabilities;

      for (let i = capabilities.length - 1; i >= 0; i--) {
        const capability = capabilities[i];

        for (
          let j = capability.selectedSubCapabilities.length - 1;
          j >= 0;
          j--
        ) {
          const subCapability = capability.selectedSubCapabilities[j];

          if (
            sameAssessmentUnit(
              capability.capabilityId,
              subCapability.subCapabilityId,
              selectedSubCapability.capabilityId,
              selectedSubCapability.subCapabilityId
            )
          ) {
            if (j - 1 >= 0) {
              const modifiedSubCapabilities = getModifiedSubCapabilities();

              if (modifiedSubCapabilities.length > 0) {
                if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
                  return;
                }

                if (
                  subCapabilityState.currentState &&
                  subCapabilityState.currentStatusDescription.trim() &&
                  subCapabilityState.futureState &&
                  subCapabilityState.futureStatusDescription.trim()
                ) {
                  await handleNextQuestion();
                }
              }

              const prevSubCapability =
                capability.selectedSubCapabilities[j - 1];
              setSelectedSubCapability({
                capabilityId: capability.capabilityId,
                subCapabilityId: prevSubCapability.subCapabilityId,
                subCapability: prevSubCapability.subCapability,
              });

              setSubSelectedCard(
                assessmentUnitKey(
                  capability.capabilityId,
                  prevSubCapability.subCapabilityId
                )
              );
              setSelectedCard(capability.capabilityId);
              setExpandedCapability(capability.capabilityId);
              return;
            }

            if (i - 1 >= 0) {
              const modifiedSubCapabilities = getModifiedSubCapabilities();

              if (modifiedSubCapabilities.length > 0) {
                if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
                  return;
                }

                if (
                  subCapabilityState.currentState &&
                  subCapabilityState.currentStatusDescription.trim() &&
                  subCapabilityState.futureState &&
                  subCapabilityState.futureStatusDescription.trim()
                ) {
                  await handleNextQuestion();
                }
              }

              const prevCapability = capabilities[i - 1];
              const lastSubCapability =
                prevCapability.selectedSubCapabilities[
                  prevCapability.selectedSubCapabilities.length - 1
                ];
              setSelectedSubCapability({
                capabilityId: prevCapability.capabilityId,
                subCapabilityId: lastSubCapability.subCapabilityId,
                subCapability: lastSubCapability.subCapability,
              });
              setSubSelectedCard(
                assessmentUnitKey(
                  prevCapability.capabilityId,
                  lastSubCapability.subCapabilityId
                )
              );
              setSelectedCard(prevCapability.capabilityId);
              setExpandedCapability(prevCapability.capabilityId);
              return;
            }
          }
        }
      }
    } else {
      alert("You are on the first question.");
    }
  };

  const handleNextQuestion = async () => {
    const nextSubCapability = findNextSubCapability();

    if (nextSubCapability) {
      setQuestionIndex((prevIndex) => prevIndex + 1);
      setSelectedSubCapability(nextSubCapability);
      setSubSelectedCard(
        assessmentUnitKey(
          nextSubCapability.capabilityId,
          nextSubCapability.subCapabilityId
        )
      );
      setSelectedCard(nextSubCapability.capabilityId);
      setExpandedCapability(nextSubCapability.capabilityId);
    }
  };

  const findNextSubCapability = () => {
    if (!data || !selectedSubCapability) return null;

    const capabilities = data.data.selectedCapabilities;

    for (let i = 0; i < capabilities.length; i++) {
      const capability = capabilities[i];

      for (let j = 0; j < capability.selectedSubCapabilities.length; j++) {
        const subCapability = capability.selectedSubCapabilities[j];
        if (
          sameAssessmentUnit(
            capability.capabilityId,
            subCapability.subCapabilityId,
            selectedSubCapability.capabilityId,
            selectedSubCapability.subCapabilityId
          )
        ) {
          if (j + 1 < capability.selectedSubCapabilities.length) {
            return {
              capabilityId: capability.capabilityId,
              subCapabilityId:
                capability.selectedSubCapabilities[j + 1].subCapabilityId,
              subCapability:
                capability.selectedSubCapabilities[j + 1].subCapability,
            };
          }
          if (i + 1 < capabilities.length) {
            const nextCapability = capabilities[i + 1];
            return {
              capabilityId: nextCapability.capabilityId,
              subCapabilityId:
                nextCapability.selectedSubCapabilities[0].subCapabilityId,
              subCapability:
                nextCapability.selectedSubCapabilities[0].subCapability,
            };
          }
        }
      }
    }

    return null;
  };

  const getSubCapabilitiesCount = () => {
    if (!data || !data.data || !data.data.selectedCapabilities) return 0;

    return data.data.selectedCapabilities.reduce(
      (total, capability) => total + capability.selectedSubCapabilities.length,
      0
    );
  };

  const getQuestionIndexFromSelection = () => {
    if (!data || !data.data || !data.data.selectedCapabilities) return 0;

    let index = 0;
    for (let i = 0; i < data.data.selectedCapabilities.length; i++) {
      const capability = data.data.selectedCapabilities[i];
      for (let j = 0; j < capability.selectedSubCapabilities.length; j++) {
        const subCapability = capability.selectedSubCapabilities[j];

        if (
          sameAssessmentUnit(
            capability.capabilityId,
            subCapability.subCapabilityId,
            selectedSubCapability.capabilityId,
            selectedSubCapability.subCapabilityId
          )
        ) {
          return index;
        }
        index++;
      }
    }
    return 0;
  };

  useEffect(() => {
    if (selectedSubCapability) {
      const newIndex = getQuestionIndexFromSelection();
      setQuestionIndex(newIndex);
    }
  }, [selectedSubCapability]);

  useEffect(() => {
    if (selectedSubCapability?.capabilityId == null) {
      return;
    }

    const unitKey = assessmentUnitKey(
      selectedSubCapability.capabilityId,
      selectedSubCapability.subCapabilityId
    );
    if (dirtySubCapabilities[unitKey]) {
      return;
    }

    const cached = lookupUnitState(
      subCapabilityStates,
      selectedSubCapability.capabilityId,
      selectedSubCapability.subCapabilityId
    );
    setSubCapabilityState(cached || EMPTY_SUB_CAPABILITY_STATE);
  }, [
    selectedSubCapability?.capabilityId,
    selectedSubCapability?.subCapabilityId,
    subCapabilityStates,
    dirtySubCapabilities,
  ]);

  useEffect(() => {
    if (selectedCard) {
      const capabilityElement = document.getElementById(`capability-${selectedCard}`);
      capabilityElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    }

    if (subSelectedCard) {
      const subCapabilityElement = document.getElementById(`sub-capability-${subSelectedCard}`);
      subCapabilityElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedCard, subSelectedCard]);

  const toggleDropdown = (capabilityId) => {
    const modifiedSubCapabilities = getModifiedSubCapabilities();

    if (modifiedSubCapabilities.length > 0) {
      if (!validateModifiedSubCapabilities(modifiedSubCapabilities)) {
        return;
      }

      if (
        subCapabilityState.currentState &&
        subCapabilityState.currentStatusDescription.trim() &&
        subCapabilityState.futureState &&
        subCapabilityState.futureStatusDescription.trim()
      ) {
        handleNextQuestion();
      }
    }

    setExpandedCapability((prev) => (prev === capabilityId ? null : capabilityId));
    setSelectedCard(capabilityId);
  };

  const handleSubmitClick = (capabilityId, subCapabilityId, subCapabilityName) => {
    setSelectedSubCapability({
      capabilityId: capabilityId,
      subCapabilityId: subCapabilityId,
      subCapability: subCapabilityName,
    });

    setSubSelectedCard(assessmentUnitKey(capabilityId, subCapabilityId));
    setSelectedCard(capabilityId);
    const cached = lookupUnitState(subCapabilityStates, capabilityId, subCapabilityId);
    setSubCapabilityState(cached || EMPTY_SUB_CAPABILITY_STATE);
    setShowModal(true);
    if (!embedded) {
      body.classList.add("modal-open");
    }
  };

  const handleInputChange = (field, value) => {
    const unitKey = assessmentUnitKey(
      selectedSubCapability.capabilityId,
      selectedSubCapability.subCapabilityId
    );

    setSubCapabilityState((prevState) => ({
      ...prevState,
      [field]: value,
    }));

    setDirtySubCapabilities((prevState) => ({
      ...prevState,
      [unitKey]: true,
    }));
  };

  const validateModifiedSubCapabilities = (modifiedSubCapabilities) => {
    const incompleteSubCapabilities = modifiedSubCapabilities.filter((subCap) => {
      return (
        !subCap.currentStatusDescription.trim() ||
        !subCap.futureStatusDescription.trim() ||
        !subCap.currentStatusRating ||
        !subCap.futureStatusRating
      );
    });

    if (incompleteSubCapabilities.length > 0) {
      alert("Complete the assessment before moving forward.");
      return false;
    }

    return true;
  };


  return (
    <div className="asses-main-container">
        <div className="asses-frame-wrapper">
          <div
            className="asses-frame-divs"
            style={embedded ? undefined : { marginLeft: "-45px" }}
          >
            <div className="asses-frame-parent1">
              <div className="asses-frame-parent4">
                <div className="asses-strategic-management-wrapper">
                  {loading ? (
                    <ContentLoader compact label="Loading assessment…" />
                  ) : !data || !data.data.selectedCapabilities.length ? (
                    <div
                      style={{
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#666",
                      }}
                    >
                      No capabilities available for assessment yet.
                    </div>
                  ) : embedded ? (
                    <div className="chat-frame-parent9">
                      {data.data.selectedCapabilities.map((capability) => {
                        const leaf = isLeafSelectedCapability(capability);
                        const firstUnit = capability.selectedSubCapabilities?.[0];
                        const selectedUnitKey = assessmentUnitKey(
                          selectedSubCapability?.capabilityId,
                          selectedSubCapability?.subCapabilityId
                        );
                        const isActive = isCapabilityNavHighlighted(
                          capability.capabilityId,
                          {
                            leaf,
                            firstSubCapabilityId: firstUnit?.subCapabilityId,
                            selectedCard,
                            subSelectedCard,
                            activeUnitKey: selectedUnitKey,
                          }
                        );
                        return (
                        <div
                          key={capability.capabilityId}
                          className={`chat-thread-list asses-frame-parent5 ${
                            isActive ? "highlight" : ""
                          }`}
                        >
                          <div className="chat-capability-card asses-capability-card">
                            <div
                              className="chat-capability-header asses-capability-header"
                              onClick={() => {
                                if (leaf && firstUnit) {
                                  handleSubmitClick(
                                    capability.capabilityId,
                                    firstUnit.subCapabilityId,
                                    firstUnit.subCapability || capability.capability
                                  );
                                  return;
                                }
                                toggleDropdown(capability.capabilityId);
                              }}
                            >
                              <h5 className="chat-capability-title">
                                {capability.capability}
                              </h5>
                              <div className="chat-capability-meta">
                                <span className="chat-capability-count">
                                  {capability.selectedSubCapabilities.length}/
                                  {capability.selectedSubCapabilities.length}
                                </span>
                                {leaf ? (
                                  isActive ? (
                                    <img
                                      src={`${basename}/images/Frame 1000005317 (3).svg`}
                                      alt=""
                                      aria-hidden="true"
                                    />
                                  ) : null
                                ) : (
                                  <img
                                    src={`${basename}/images/down.svg`}
                                    alt=""
                                    aria-hidden="true"
                                    className={
                                      expandedCapability === capability.capabilityId
                                        ? "is-expanded"
                                        : ""
                                    }
                                  />
                                )}
                              </div>
                            </div>
                            {!leaf && expandedCapability === capability.capabilityId && (
                              <div
                                ref={(el) =>
                                  (dropdownRefs.current[capability.capabilityId] =
                                    el)
                                }
                                className="asses-sub-capability-dropdown"
                              >
                                {capability.selectedSubCapabilities.map(
                                  (subCapability) => {
                                    const unitKey = assessmentUnitKey(
                                      capability.capabilityId,
                                      subCapability.subCapabilityId
                                    );
                                    return (
                                    <a
                                      className={`chat-frame-parent10 chat-sub-capability-link asses-frame-parent6 ${
                                        subSelectedCard === unitKey ||
                                        selectedUnitKey === unitKey
                                          ? "highlight is-selected"
                                          : ""
                                      }`}
                                      key={unitKey}
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleSubmitClick(
                                          capability.capabilityId,
                                          subCapability.subCapabilityId,
                                          subCapability.subCapability
                                        );
                                      }}
                                    >
                                      <span className="chat-sub-capability-link__label">
                                        {subCapability.subCapability}
                                      </span>
                                      <span className="chat-sub-capability-link__meta">
                                        <img
                                          src={`${basename}/images/Frame 1000005317 (3).svg`}
                                          alt=""
                                          aria-hidden="true"
                                        />
                                      </span>
                                    </a>
                                    );
                                  }
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    data.data.selectedCapabilities.map((capability) => {
                      const leaf = isLeafSelectedCapability(capability);
                      const firstUnit = capability.selectedSubCapabilities?.[0];
                      const selectedUnitKey = assessmentUnitKey(
                        selectedSubCapability?.capabilityId,
                        selectedSubCapability?.subCapabilityId
                      );
                      const isActive = isCapabilityNavHighlighted(
                        capability.capabilityId,
                        {
                          leaf,
                          firstSubCapabilityId: firstUnit?.subCapabilityId,
                          selectedCard,
                          subSelectedCard,
                          activeUnitKey: selectedUnitKey,
                        }
                      );
                      return (
                      <div
                        className={`asses-frame-parent5 ${
                          isActive ? "highlight" : ""
                        }`}
                        key={capability.capabilityId}
                      >
                        <div
                          style={{
                            width: "100%",
                            border: "1px solid #ddd",
                            borderRadius: "8px",
                            padding: "8px 15px 8px 15px",
                            backgroundColor: "#fff",
                          }}
                        >
                          <div
                            className="asses-capability-header"
                            onClick={() => {
                              if (leaf && firstUnit) {
                                handleSubmitClick(
                                  capability.capabilityId,
                                  firstUnit.subCapabilityId,
                                  firstUnit.subCapability || capability.capability
                                );
                                return;
                              }
                              toggleDropdown(capability.capabilityId);
                            }}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              width: "100%",
                              marginTop: "10px",
                            }}
                          >
                            <h5
                              style={{
                                flex: 1,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontSize: "14px",
                              }}
                            >
                              {capability.capability}
                            </h5>
                            <span
                              style={{
                                marginLeft: "10px",
                                marginTop: "-8px",
                                flexShrink: 0,
                              }}
                            >
                              {capability.selectedSubCapabilities.length} /{" "}
                              {capability.selectedSubCapabilities.length}
                            </span>
                            {leaf ? (
                              isActive ? (
                                <img
                                  src={`${basename}/images/Frame 1000005317 (3).svg`}
                                  alt=""
                                  aria-hidden
                                  style={{
                                    marginLeft: "10px",
                                    marginTop: "-8px",
                                  }}
                                />
                              ) : null
                            ) : (
                              <img
                                src={`${basename}/images/down.svg`}
                                alt="Expand"
                                style={{
                                  marginLeft: "10px",
                                  marginTop: "-8px",
                                  transform:
                                    expandedCapability === capability.capabilityId
                                      ? "rotate(180deg)"
                                      : "rotate(0deg)",
                                  transition: "transform 0.3s",
                                }}
                              />
                            )}
                          </div>
                          {!leaf && expandedCapability === capability.capabilityId && (
                            <div
                              ref={(el) =>
                                (dropdownRefs.current[capability.capabilityId] =
                                  el)
                              }
                              className="asses-sub-capability-dropdown"
                            >
                              {capability.selectedSubCapabilities.map(
                                (subCapability) => {
                                  const unitKey = assessmentUnitKey(
                                    capability.capabilityId,
                                    subCapability.subCapabilityId
                                  );
                                  return (
                                  <a
                                    className={`asses-frame-parent6 ${
                                      subSelectedCard === unitKey ? "highlight" : ""
                                    }`}
                                    key={unitKey}
                                    href="#"
                                    onClick={() =>
                                      handleSubmitClick(
                                        capability.capabilityId,
                                        subCapability.subCapabilityId,
                                        subCapability.subCapability
                                      )
                                    }
                                    style={{
                                      textDecoration: "none",
                                      color: "#333",
                                      display: "block",
                                      margin: "5px 0",
                                      padding: "10px",
                                      borderRadius: "6px",
                                      backgroundColor: "#fff",
                                    }}
                                  >
                                    {subCapability.subCapability}
                                    <img
                                      src={`${basename}/images/Frame 1000005317 (3).svg`}
                                      alt="Edit"
                                      style={{ marginLeft: "10px" }}
                                    />
                                  </a>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })
                  )}
                </div>
                {showModal && (
                  <div className="asses-frame-wrapper12">
                    <div className="asses-sub-capability-dataai-vision-parent">
                      <div className="asses-sub-capability-header">
                        <div className="asses-strategic-management">
                          <h5>
                            {assessmentUnitTitle(
                              selectedSubCapability?.capabilityId,
                              selectedSubCapability?.subCapabilityId,
                              selectedSubCapability?.subCapability
                            )}
                          </h5>
                        </div>
                      </div>
                      <div className="asses-vector-parent">
                        <img
                          className="asses-vector-icon"
                          alt=""
                          src={`${basename}/images/Vector 2.svg`}
                        />
                        <div className="asses-frame-parent10">
                          <div className="asses-frame-parent11">
                            <div className="asses-current-state-parent">
                              <div className="asses-strategic-management required">
                                {formConfig.currentSection.title}
                              </div>
                              <div className="asses-describe-the-current">
                                {formConfig.currentSection.description}
                              </div>
                            </div>
                            {formConfig.maturityStateKeys.map((state) => (
                              <div key={state} className="asses-radio-row">
                                <input
                                  type="radio"
                                  id={`current-${state}`}
                                  name="currentState"
                                  value={state}
                                  checked={
                                    subCapabilityState.currentState === state
                                  }
                                  onChange={() =>
                                    handleInputChange("currentState", state)
                                  }
                                  disabled={'disabled'}
                                />
                                <label
                                  htmlFor={`current-${state}`}
                                  className="asses-radio-label"
                                >
                                  {getMaturityLabel(state, formConfig)}
                                </label>
                                <div
                                  className="asses-help-trigger"
                                  onMouseOver={() => {
                                    const tooltip = document.getElementById(
                                      `tooltip-current-${state}`
                                    );
                                    if (tooltip)
                                      tooltip.style.display = "block";
                                  }}
                                  onMouseOut={() => {
                                    const tooltip = document.getElementById(
                                      `tooltip-current-${state}`
                                    );
                                    if (tooltip) tooltip.style.display = "none";
                                  }}
                                >
                                  <img
                                    className="asses-help-icon"
                                    alt="Help"
                                    src={`${basename}/images/excla.svg`}
                                  />
                                  <div
                                    id={`tooltip-current-${state}`}
                                    className="asses-state-tooltip"
                                  >
                                    {formConfig.stateDescriptions[state]}
                                  </div>
                                </div>
                              </div>
                            ))}

                            <div className="asses-frame-wrapper13">
                              <label className="asses-comment-label required">
                                Comment
                              </label>
                              <textarea
                                className="asses-custom-textarea asses-state-comment"
                                placeholder={formConfig.currentSection.commentPlaceholder}
                                style={{ whiteSpace: "pre-wrap" }}
                                value={
                                  subCapabilityState.currentStatusDescription
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "currentStatusDescription",
                                    e.target.value
                                  )
                                }
                                disabled={'disabled'}
                              />
                            </div>
                          </div>
                          <img
                            style={{ marginLeft: "0" }}
                            alt=""
                            src={`${basename}/images/VerticalVector2.svg`}
                          />
                          <div className="asses-frame-parent11">
                            <div className="asses-current-state-parent">
                              <div className="asses-strategic-management required">
                                {formConfig.futureSection.title}
                              </div>
                              <div className="asses-describe-the-current">
                                {formConfig.futureSection.description}
                              </div>
                            </div>
                            {formConfig.maturityStateKeys.map((state) => (
                              <div key={`future-${state}`} className="asses-radio-row">
                                <input
                                  type="radio"
                                  id={`future-${state}`}
                                  name="futureState"
                                  value={state}
                                  checked={
                                    subCapabilityState.futureState === state
                                  }
                                  onChange={() =>
                                    handleInputChange("futureState", state)
                                  }
                                  disabled={'disabled'}
                                />
                                <label
                                  htmlFor={`future-${state}`}
                                  className="asses-radio-label"
                                >
                                  {getMaturityLabel(state, formConfig)}
                                </label>
                                <div
                                  className="asses-help-trigger"
                                  onMouseOver={() => {
                                    const tooltip = document.getElementById(
                                      `tooltip-future-${state}`
                                    );
                                    if (tooltip)
                                      tooltip.style.display = "block";
                                  }}
                                  onMouseOut={() => {
                                    const tooltip = document.getElementById(
                                      `tooltip-future-${state}`
                                    );
                                    if (tooltip) tooltip.style.display = "none";
                                  }}
                                >
                                  <img
                                    className="asses-help-icon"
                                    alt="Help"
                                    src={`${basename}/images/excla.svg`}
                                  />
                                  <div
                                    id={`tooltip-future-${state}`}
                                    className="asses-state-tooltip"
                                  >
                                    {formConfig.stateDescriptions[state]}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <div className="asses-frame-wrapper13">
                              <label className="asses-comment-label required">
                                Comment
                              </label>
                              <textarea
                                className="asses-custom-textarea asses-state-comment"
                                placeholder={formConfig.futureSection.commentPlaceholder}
                                style={{ whiteSpace: "pre-wrap" }}
                                value={
                                  subCapabilityState.futureStatusDescription
                                }
                                onChange={(e) =>
                                  handleInputChange(
                                    "futureStatusDescription",
                                    e.target.value
                                  )
                                }
                                disabled={'disabled'}
                              />
                            </div>
                            
                            {/* Business Value and Impact Text Boxes */}
                            <div style={{ marginTop: "10px", width: "100%" }}>
                              <div style={{ marginBottom: "8px", width: "100%" }}>
                                <div className="asses-strategic-management">
                                  {formConfig.businessValueTitle}
                                </div>
                                <textarea
                                  className="asses-custom-textarea"
                                  placeholder={formConfig.businessValuePlaceholder}
                                  style={{ 
                                    whiteSpace: "pre-wrap", 
                                    height: "60px",
                                    fontSize: "12px",
                                    resize: "none",
                                    width: "100%"
                                  }}
                                  value={subCapabilityState.businessValue}
                                  onChange={(e) =>
                                    handleInputChange("businessValue", e.target.value)
                                  }
                                  disabled={'disabled'}
                                />
                              </div>
                              <div style={{ width: "100%" }}>
                                <div className="asses-strategic-management">
                                  {formConfig.businessImpactTitle}
                                </div>
                                <div style={{ position: "relative", width: "100%" }}>
                                  <input
                                    type="text"
                                    placeholder={formConfig.businessImpactPlaceholder}
                                    style={{ 
                                      width: "100%",
                                      height: "40px",
                                      fontSize: "12px",
                                      padding: "8px",
                                      border: "1px solid #ccc",
                                      borderRadius: "4px",
                                      backgroundColor: "white"
                                    }}
                                    value={subCapabilityState.businessValueImpact ? `${subCapabilityState.businessValueImpact}%` : ""}
                                    onChange={(e) => {
                                      const value = e.target.value.replace('%', '');
                                      // Allow only numbers, max 100
                                      if (value === "" || /^\d{1,3}$/.test(value) && parseFloat(value) <= 100) {
                                        handleInputChange("businessValueImpact", value);
                                      }
                                    }}
                                    disabled={'disabled'}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {
                        <AssessmentQuestionNav
                          questionIndex={questionIndex}
                          totalQuestions={getSubCapabilitiesCount()}
                          onPrevious={handleBackQuestion}
                          onNext={handleNextQuestion}
                        />
                      }
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default Assess;
